/**
 * Vad som är påslaget.
 *
 * Första upplagan säljs precis som förr: en tryckt lapp med QR-kod,
 * aktiveringskod och en kort instruktion. Allt annat vi byggt – digital
 * beställning, registrering av lappar, föräldrarnas egen vy – finns kvar i
 * koden men är avstängt tills föreningen vill ta nästa steg.
 *
 * Flaggorna finns för att alternativet är sämre. Att riva ut funktionerna
 * skulle betyda att bygga dem igen; att lämna dem påslagna skulle betyda att
 * föräldrar möter två sätt att få ett häfte under samma säsong. En strömbrytare
 * är det enda som håller båda dörrarna öppna.
 *
 * Standardläget är avstängt, och det är medvetet: en flagga som saknas ska
 * betyda "inte lanserad än", inte "på". Ett nytt fält i databasen, ett
 * misslyckat anrop eller en tom installation ska alla ge samma trygga svar.
 */

export type FeatureKey =
  | 'digitalOrders'
  | 'digitalSale'
  | 'slipRegistration'
  | 'loginValue'
  | 'signUp'

export interface Features {
  /** Beställningssidan, lagets länk, påminnelser och Swish-avstämningen. */
  digitalOrders: boolean
  /** Lagledaren mejlar en kod direkt till en köpare. */
  digitalSale: boolean
  /** Föräldrar registrerar sina papperslappar och ser dem under Mina häften. */
  slipRegistration: boolean
  /** Raden på inloggningssidan som säger vad häftet är värt. */
  loginValue: boolean
  /** Att det går att skapa ett konto. Av fram till lanseringen. */
  signUp: boolean
}

/** Allt av är utgångsläget – se resonemanget överst. */
export const DEFAULT_FEATURES: Features = {
  digitalOrders: false,
  digitalSale: false,
  slipRegistration: false,
  loginValue: false,
  signUp: false,
}

export const FEATURE_KEYS: FeatureKey[] = [
  'digitalOrders',
  'digitalSale',
  'slipRegistration',
  'loginValue',
  'signUp',
]

export const FEATURE_LABELS: Record<FeatureKey, string> = {
  digitalOrders: 'Digital beställning',
  digitalSale: 'Digital försäljning från lagvyn',
  slipRegistration: 'Registrering av fysiska lappar',
  loginValue: 'Häftets värde på inloggningssidan',
  signUp: 'Att skapa konto',
}

/** Vad som händer när den slås på, sagt till den som ska fatta beslutet. */
export const FEATURE_DESCRIPTIONS: Record<FeatureKey, string> = {
  digitalOrders:
    'Föräldrar beställer häften via lagets länk och betalar med Swish innan de får sina koder. ' +
    'Lagledaren får en lista med beställningar, kan påminna om obetalda och stämma av mot en ' +
    'Swish-rapport. Tre saker behöver vara på plats först: köpvillkor med ångerrätt, som ' +
    'distansavtalslagen kräver vid försäljning till konsument, att spelarens namn blir ' +
    'frivilligt i formuläret, och skräpskyddet: beställningssidan är den enda öppna vägen in ' +
    'som skickar ett mejl vid varje anrop, så utan reCAPTCHA-nyckel i VITE_RECAPTCHA_SITE_KEY ' +
    'är föreningens avsändare en gratis mejlrelä för den som hittar adressen. Integritets' +
    'policyn behöver skrivas om samtidigt – den beskriver i dag pappersupplagan och säger ' +
    'uttryckligen att vi varken samlar in namn, barnens namn, beställningar eller använder ' +
    'skräpskydd. Allt det blir osant i samma stund som den här slås på.',
  digitalSale:
    'Lagledaren knyter en kod till en köpares mejladress och skickar den direkt från lagvyn. ' +
    'Kräver att utskicket av mejl är uppsatt – utan det hamnar mejlet i kö och köparen får ' +
    'aldrig sin kod, medan lagledaren ser att det gick igenom.',
  slipRegistration:
    'Föräldrar registrerar koderna på lapparna de fått, och ser under Mina häften vilka de ' +
    'delat ut och hur mycket av utlägget som kommit tillbaka. Lagledaren kan registrera åt ' +
    'den som inte gör det själv.',
  loginValue:
    'Inloggningssidan visar hur många rabatter häftet innehåller och ungefär vad de är värda, ' +
    'så att den som står med en kod ser vad hen får innan hen skapar konto.',
  signUp:
    'Att nya konton går att skapa. Håll den avstängd tills häftena delats ut – annars kan den ' +
    'som hittar adressen i förväg registrera sig, och ett konto utan kod kan ingenting ändå. ' +
    'Slå på den samma dag lapparna går ut, för utan konto går det inte att aktivera. ' +
    'Spärren sitter i appen och inte i Firebase: den stoppar varje förälder, men inte den som ' +
    'medvetet anropar Googles API förbi gränssnittet. Att stänga av inloggningssättet helt i ' +
    'Firebase-konsolen vore den hårda spärren, men då kommer inte administratörerna in heller.',
}

/**
 * Vad som gäller oavsett flaggor, sagt en gång så att ingen behöver gissa.
 *
 * Det här är första upplagan: köparen aktiverar sin kod, bläddrar och löser in
 * i butiken, lagledaren bockar av betalt mot lagkassan, och admin sköter
 * företag, erbjudanden, perioder och kodserier. Ingen flagga stänger av något
 * av det.
 */
export const ALWAYS_ON =
  'Aktivering, inlösen, lagets kodlista, avbockning av betalt, statistik och adminytan ' +
  'påverkas inte av de här strömbrytarna.'

/** Läser en flagga ur ett dokument som kan vara ofullständigt eller saknas. */
export function featureEnabled(
  features: Partial<Features> | null | undefined,
  key: FeatureKey,
): boolean {
  return features?.[key] === true
}

/** Fyller ut ett halvt dokument till en hel uppsättning. */
export function withDefaults(features: Partial<Features> | null | undefined): Features {
  return {
    digitalOrders: featureEnabled(features, 'digitalOrders'),
    digitalSale: featureEnabled(features, 'digitalSale'),
    slipRegistration: featureEnabled(features, 'slipRegistration'),
    loginValue: featureEnabled(features, 'loginValue'),
    signUp: featureEnabled(features, 'signUp'),
  }
}
